/*
* BarChart.jsx
* Copyright (c) Harman International Industries, Incorporated, 2024 - 2025
* All rights reserved.
*/
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { BarChart } from "@mui/x-charts/BarChart";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import { dataset, valueFormatter } from "./dataset/weather";
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { chartsGridClasses } from "@mui/x-charts/ChartsGrid";
import Moment from "moment";

export const BarChartComponent = ({ barAndPieObjData }) => {
  const [installsEachDay, setInstallsEachDay] = useState([]);
  const [uninstallsEachDay, setUninstallsEachDay] = useState([]);

  const [installAlignment, setInstallAlignment] = useState("daily");
  const [uninstallAlignment, setUninstallAlignment] = useState("daily");

  const handleChange = (event, newAlignment) => {
    setInstallAlignment(newAlignment);
  };

  const handleUninstallChange = (event, newAlignment) => {
    setUninstallAlignment(newAlignment);
  };

  useEffect(() => {
    let installsDayObj = [];
    let processedData = [];
    setInstallsEachDay([]);
    setUninstallsEachDay([]);
    if (
      barAndPieObjData !== undefined &&
      Object.keys(barAndPieObjData.installsEachDay).length > 0
    ) {
      installsDayObj = barAndPieObjData.installsEachDay.map((installVal) => {
        let obj = {
          formatteddate: Moment(installVal.date).format("DD MMM"),
          numberOfInstalls: installVal.numberOfInstalls,
          date: installVal.date,
        };

        return obj;
      });

      processedData = installsDayObj.filter(
        (obj) => !Object.values(obj).includes(0)
      );

      setInstallsEachDay(processedData);
    }

    if (
      barAndPieObjData !== undefined &&
      Object.keys(barAndPieObjData.uninstallsEachDay).length > 0
    ) {
      let uninstallsDayObj = barAndPieObjData.uninstallsEachDay.map(
        (uninstallVal) => {
          let obj = {
            formatteddate: Moment(uninstallVal.date).format("DD MMM"),
            numberOfUnInstalls: uninstallVal.numberOfUnInstalls,
            date: uninstallVal.date,
          };
          return obj;
        }
      );
      processedData = uninstallsDayObj.filter(
        (obj) => !Object.values(obj).includes(0)
      );

      setUninstallsEachDay(processedData);
    }
  }, [barAndPieObjData]);

  return (
    <div className="cardBarCls">
      <Card
        sx={{
          width: 875,
          height: 300,
          boxShadow: "none",
          border: "1px solid #12141533",
        }}
      >
        <CardHeader
          action={
              <ToggleButtonGroup
                color="primary"
                value={installAlignment}
                exclusive
                onChange={handleChange}
                aria-label="Action"
              >
                <ToggleButton className="actionBtnCls1" value="daily">
                  <span className="actionBtnCls1SpanText">Daily</span>
                  <span className="actionBtnCls1SpanCount">0</span>
                </ToggleButton>
                <ToggleButton className="actionBtnCls1" value="total">
                  <span className="actionBtnCls1SpanText">Total</span>
                  <span className="actionBtnCls1SpanCount">0</span>
                </ToggleButton>
              </ToggleButtonGroup>
          }
          title="Installs"
          sx={{
            padding: "3px",
            paddingLeft: "10px",
            fontWeight: 600,
          }}
          disableTypography
        />
        <Divider sx={{marginTop: "0px !important"}} />
        <CardContent sx={{ textAlign: "center" }}>
          <BarChart
            dataset={installsEachDay}
            grid={{ horizontal: true }}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "formatteddate",
                colorMap: {
                  type: "piecewise",
                  thresholds: [],
                  colors: ["#0476E2"],
                },
              },
            ]}
            series={[
              {
                dataKey: "numberOfInstalls",
              },
            ]}
            {...chartSetting}
            sx={{
              "& .MuiBarElement-root": {
                width: "20px !important",
              },
              [`& .${chartsGridClasses.line}`]: {
                strokeDasharray: "5 3",
                strokeWidth: 2,
              },
            }}
          />
        </CardContent>
      </Card>

      <Card
        sx={{
          width: 875,
          height: 300,
          boxShadow: "none",
          border: "1px solid #12141533",
        }}
      >
        <CardHeader
          action={
              <ToggleButtonGroup
                color="primary"
                value={uninstallAlignment}
                exclusive
                onChange={handleUninstallChange}
                aria-label="Action"
              >
                <ToggleButton className="actionBtnCls1" value="daily">
                  <span className="actionBtnCls1SpanText">Daily</span>
                  <span className="actionBtnCls1SpanCount">0</span>
                </ToggleButton>
                <ToggleButton className="actionBtnCls1" value="total">
                  <span className="actionBtnCls1SpanText">Total</span>
                  <span className="actionBtnCls1SpanCount">0</span>
                </ToggleButton>
              </ToggleButtonGroup>
          }
          title="Uninstalls"
          sx={{
            padding: "3px",
            paddingLeft: "10px",
            fontWeight: 600,
          }}
          disableTypography
        />
        <Divider sx={{marginTop: "0px !important"}} />
        <CardContent sx={{ textAlign: "center" }}>
          <BarChart
            dataset={uninstallsEachDay}
            grid={{ horizontal: true }}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "formatteddate",
                colorMap: {
                  type: "piecewise",
                  thresholds: [],
                  colors: ["#0476E2"],
                },
              },
            ]}
            series={[
              {
                dataKey: "numberOfUnInstalls",
                valueFormatter,
              },
            ]}
            {...chartSetting}
            sx={{
              "& .MuiBarElement-root": {
                width: "20px !important",
              },
              [`& .${chartsGridClasses.line}`]: {
                strokeDasharray: "5 3",
                strokeWidth: 2,
              },
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
};

BarChartComponent.propTypes = {
  barAndPieObjData: PropTypes.shape({
    installsEachDay: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string.isRequired,
        numberOfInstalls: PropTypes.number.isRequired,
      })
    ).isRequired,
    uninstallsEachDay: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string.isRequired,
        numberOfUnInstalls: PropTypes.number.isRequired,
      })
    ).isRequired,
  }),
};

const chartSetting = {
  // yAxis: [
  //   {
  //     label: "rainfall (mm)",
  //   },
  // ],
  width: 875,
  height: 200,
  sx: {
    [`.${axisClasses.left} .${axisClasses.label}`]: {
      transform: "translate(-10px, 0)",
    },
  },
};
